/**
 * Frontend validation utilities that align with the OpenAPI schema requirements
 * for the coral tracking API.
 */

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * Validates an RFID according to OpenAPI schema:
 * - Exactly 24 characters
 * - Only alphanumeric characters (a-z, A-Z, 0-9)
 */
export const validateRfid = (rfid: string): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!rfid || typeof rfid !== 'string') {
    errors.push({
      field: 'rfid',
      message: 'RFID is required',
      value: rfid,
    });
  } else {
    // Trim whitespace for validation
    const trimmedRfid = rfid.trim();

    if (trimmedRfid.length !== 24) {
      errors.push({
        field: 'rfid',
        message: `RFID must be exactly 24 characters (got ${trimmedRfid.length})`,
        value: trimmedRfid,
      });
    }

    if (!/^[a-zA-Z0-9]{24}$/.test(trimmedRfid)) {
      errors.push({
        field: 'rfid',
        message: 'RFID must contain only alphanumeric characters (letters and numbers)',
        value: trimmedRfid,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates a species code according to OpenAPI schema:
 * - Required field
 * - 1-32 characters
 * - Only uppercase letters, numbers, underscore, and hyphen
 */
export const validateSpeciesCode = (speciesCode: string): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!speciesCode || typeof speciesCode !== 'string') {
    errors.push({
      field: 'speciesCode',
      message: 'Species Code is required',
      value: speciesCode,
    });
  } else {
    const trimmedCode = speciesCode.trim();

    if (trimmedCode.length < 1 || trimmedCode.length > 32) {
      errors.push({
        field: 'speciesCode',
        message: `Species Code must be 1-32 characters (got ${trimmedCode.length})`,
        value: trimmedCode,
      });
    }

    if (!/^[A-Z0-9_-]+$/.test(trimmedCode)) {
      errors.push({
        field: 'speciesCode',
        message:
          'Species Code must contain only uppercase letters, numbers, underscores, and hyphens',
        value: trimmedCode,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates a cut date according to OpenAPI schema:
 * - Optional field (nullable)
 * - YYYY-MM format
 * - Maximum 7 characters
 */
export const validateCutDate = (cutDate: string | null | undefined): ValidationResult => {
  const errors: ValidationError[] = [];

  // Cut date is optional/nullable
  if (cutDate === null || cutDate === undefined || cutDate === '') {
    return { isValid: true, errors: [] };
  }

  if (typeof cutDate !== 'string') {
    errors.push({
      field: 'cutDate',
      message: 'Cut Date must be a string in YYYY-MM format',
      value: cutDate,
    });
  } else {
    const trimmedDate = cutDate.trim();

    if (trimmedDate.length > 7) {
      errors.push({
        field: 'cutDate',
        message: `Cut Date must be maximum 7 characters (got ${trimmedDate.length})`,
        value: trimmedDate,
      });
    }

    if (!/^\d{4}-\d{2}$/.test(trimmedDate)) {
      errors.push({
        field: 'cutDate',
        message: 'Cut Date must be in YYYY-MM format (e.g., 2024-12)',
        value: trimmedDate,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates status according to OpenAPI schema:
 * - Must be one of: ALIVE, DECEASED, EXPORTED
 */
export const validateStatus = (status: string): ValidationResult => {
  const errors: ValidationError[] = [];
  const validStatuses = ['ALIVE', 'DECEASED', 'EXPORTED'];

  if (!status || typeof status !== 'string') {
    errors.push({
      field: 'status',
      message: 'Status is required',
      value: status,
    });
  } else {
    const trimmedStatus = status.trim().toUpperCase();

    if (!validStatuses.includes(trimmedStatus)) {
      errors.push({
        field: 'status',
        message: `Status must be one of: ${validStatuses.join(', ')}`,
        value: status,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates export ID according to OpenAPI schema:
 * - Optional field (nullable)
 * - Must be a valid UUID format if provided
 */
export const validateExportId = (exportId: string | null | undefined): ValidationResult => {
  const errors: ValidationError[] = [];

  // Export ID is optional/nullable
  if (exportId === null || exportId === undefined || exportId === '') {
    return { isValid: true, errors: [] };
  }

  if (typeof exportId !== 'string') {
    errors.push({
      field: 'exportId',
      message: 'Export ID must be a valid UUID',
      value: exportId,
    });
  } else {
    const trimmedId = exportId.trim();
    const uuidPattern =
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

    if (!uuidPattern.test(trimmedId)) {
      errors.push({
        field: 'exportId',
        message:
          'Export ID must be a valid UUID format (e.g., 123e4567-e89b-12d3-a456-426614174000)',
        value: trimmedId,
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates an array of RFIDs according to OpenAPI schema:
 * - Must have at least 1 item (minItems: 1)
 * - Each RFID must pass individual validation
 */
export const validateRfidArray = (rfids: string[]): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!Array.isArray(rfids)) {
    errors.push({
      field: 'rfid_array',
      message: 'RFID array must be an array',
      value: rfids,
    });
    return { isValid: false, errors };
  }

  if (rfids.length < 1) {
    errors.push({
      field: 'rfid_array',
      message: 'At least one RFID is required',
      value: rfids,
    });
  }

  // Validate each RFID in the array
  rfids.forEach((rfid, index) => {
    const rfidValidation = validateRfid(rfid);
    if (!rfidValidation.isValid) {
      rfidValidation.errors.forEach((error) => {
        errors.push({
          field: `rfid_array[${index}]`,
          message: error.message,
          value: error.value,
        });
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validates a complete CreateCoralRequest payload according to OpenAPI schema
 */
export const validateCreateCoralRequest = (payload: {
  speciesCode: string;
  rfid_array: string[];
  cutDate?: string | null;
  status?: string;
  exportId?: string | null;
}): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate required fields
  const speciesValidation = validateSpeciesCode(payload.speciesCode);
  if (!speciesValidation.isValid) {
    errors.push(...speciesValidation.errors);
  }

  const rfidArrayValidation = validateRfidArray(payload.rfid_array);
  if (!rfidArrayValidation.isValid) {
    errors.push(...rfidArrayValidation.errors);
  }

  // Validate optional fields if provided
  if (payload.cutDate !== undefined) {
    const cutDateValidation = validateCutDate(payload.cutDate);
    if (!cutDateValidation.isValid) {
      errors.push(...cutDateValidation.errors);
    }
  }

  if (payload.status !== undefined) {
    const statusValidation = validateStatus(payload.status);
    if (!statusValidation.isValid) {
      errors.push(...statusValidation.errors);
    }
  }

  if (payload.exportId !== undefined) {
    const exportIdValidation = validateExportId(payload.exportId);
    if (!exportIdValidation.isValid) {
      errors.push(...exportIdValidation.errors);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Formats validation errors into a user-friendly message
 */
export const formatValidationErrors = (errors: ValidationError[]): string => {
  if (errors.length === 0) {
    return '';
  }

  if (errors.length === 1) {
    return errors[0].message;
  }

  return errors.map((error) => `• ${error.message}`).join('\n');
};

/**
 * Quick RFID validation for real-time feedback during scanning
 * Returns a simple boolean for immediate UI feedback
 */
export const isValidRfid = (rfid: string): boolean => {
  if (!rfid || typeof rfid !== 'string') {
    return false;
  }

  const trimmedRfid = rfid.trim();
  return trimmedRfid.length === 24 && /^[a-zA-Z0-9]{24}$/.test(trimmedRfid);
};

/**
 * Quick species code validation for real-time feedback
 * Returns a simple boolean for immediate UI feedback
 */
export const isValidSpeciesCode = (speciesCode: string): boolean => {
  if (!speciesCode || typeof speciesCode !== 'string') {
    return false;
  }

  const trimmedCode = speciesCode.trim();
  return trimmedCode.length >= 1 && trimmedCode.length <= 32 && /^[A-Z0-9_-]+$/.test(trimmedCode);
};
